<script lang="ts">
  import { Button, Input } from '@aatmanova/ui';

  interface TaxonomyConfig {
    mode: 'category' | 'subcategory';
    action: 'create' | 'edit';
    item?: any;
    parentId?: string;
  }

  interface Props {
    categories: any[];
    onCreateCategory: (data: { name: string; slug: string; description?: string }) => Promise<any>;
    onUpdateCategory: (id: string, data: { name?: string; slug?: string; description?: string }) => Promise<any>;
    onDeleteCategory: (id: string) => Promise<any>;
    onCreateSubcategory: (data: { categoryId: string; name: string; slug: string; description?: string }) => Promise<any>;
    onUpdateSubcategory: (id: string, data: { name?: string; slug?: string; description?: string }) => Promise<any>;
    onDeleteSubcategory: (id: string) => Promise<any>;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  }

  let {
    categories,
    onCreateCategory,
    onUpdateCategory,
    onDeleteCategory,
    onCreateSubcategory,
    onUpdateSubcategory,
    onDeleteSubcategory,
    addToast
  }: Props = $props();

  // Expand/Collapse state
  let expandedCategories = $state<string[]>([]);
  function toggleExpand(id: string) {
    expandedCategories = expandedCategories.includes(id)
      ? expandedCategories.filter(x => x !== id)
      : [...expandedCategories, id];
  }

  // Modal state
  let isModalOpen = $state(false);
  let config = $state<TaxonomyConfig>({ mode: 'category', action: 'create' });
  let taxName = $state('');
  let taxSlug = $state('');
  let taxDescription = $state('');
  let isSaving = $state(false);

  function openModal(mode: TaxonomyConfig['mode'], action: TaxonomyConfig['action'], item?: any, parentId?: string) {
    config = { mode, action, item, parentId };
    taxName = item?.name || '';
    taxSlug = item?.slug || '';
    taxDescription = item?.description || '';
    isModalOpen = true;
  }

  $effect(() => {
    if (config.action === 'create' && taxName) {
      taxSlug = taxName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }
  });

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    isSaving = true;
    try {
      const isSub = config.mode === 'subcategory';
      const isEdit = config.action === 'edit';
      const payload = { name: taxName, slug: taxSlug, description: taxDescription };

      if (isSub) {
        if (isEdit) await onUpdateSubcategory(config.item.id, payload);
        else await onCreateSubcategory({ categoryId: config.parentId!, ...payload });
      } else {
        if (isEdit) await onUpdateCategory(config.item.id, payload);
        else await onCreateCategory(payload);
      }

      isModalOpen = false;
      addToast(`${isSub ? 'Subcategory' : 'Category'} saved successfully.`, 'success');
    } catch (err: any) {
      addToast(err.message || 'Operation failed', 'error');
    } finally {
      isSaving = false;
    }
  }

  // Delete state
  let itemToDelete = $state<{ mode: 'category' | 'subcategory'; id: string; name: string } | null>(null);
  let isDeleting = $state(false);

  async function handleConfirmDelete() {
    if (!itemToDelete) return;
    isDeleting = true;
    try {
      if (itemToDelete.mode === 'subcategory') await onDeleteSubcategory(itemToDelete.id);
      else await onDeleteCategory(itemToDelete.id);
      addToast(`${itemToDelete.mode === 'subcategory' ? 'Subcategory' : 'Category'} deleted.`, 'success');
      itemToDelete = null;
    } catch (err: any) {
      addToast(err.message || 'Delete failed', 'error');
    } finally {
      isDeleting = false;
    }
  }
</script>

<div class="space-y-8 animate-fadeIn">
  <!-- Stats Summary -->
  <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
    <div class="card bg-base-100 border border-base-300 p-6 shadow-xl">
      <h4 class="text-xs font-bold text-base-content/50 uppercase tracking-wider mb-1">Parent Categories</h4>
      <p class="text-3xl font-bold text-primary">{categories.length}</p>
    </div>
    <div class="card bg-base-100 border border-base-300 p-6 shadow-xl">
      <h4 class="text-xs font-bold text-base-content/50 uppercase tracking-wider mb-1">Subcategories</h4>
      <p class="text-3xl font-bold text-secondary">
        {categories.reduce((acc, c) => acc + (c.subcategories?.length || 0), 0)}
      </p>
    </div>
    <div class="card bg-base-100 border border-base-300 p-6 shadow-xl">
      <h4 class="text-xs font-bold text-base-content/50 uppercase tracking-wider mb-1">Managed SKUs</h4>
      <p class="text-3xl font-bold text-accent">
        {categories.reduce((acc, c) => acc + (c._count?.products || 0), 0)}
      </p>
    </div>
  </div>

  <!-- Taxonomy Tree Header -->
  <div class="flex items-center justify-between">
    <h3 class="text-lg font-bold text-base-content uppercase tracking-tight">Taxonomy Directory</h3>
    <Button onclick={() => openModal('category', 'create')} variant="primary" size="sm" class="font-bold">
      + Create Category
    </Button>
  </div>

  <!-- Category Tree -->
  <div class="space-y-4">
    {#if categories.length === 0}
      <div class="text-center py-20 bg-base-100 border-2 border-dashed border-base-300 rounded-3xl">
        <svg class="w-12 h-12 text-base-content/30 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
        </svg>
        <h3 class="text-lg font-bold text-base-content/70">No Categories Found</h3>
        <p class="text-xs text-base-content/40 mt-1">Add your first category to get started.</p>
      </div>
    {:else}
      {#each categories as category}
        <div class="card bg-base-100 border border-base-300 rounded-3xl overflow-hidden shadow-md transition-all duration-300">
          <!-- Category Row -->
          <div class="p-5 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-base-200/50 transition-colors gap-4">
            <!-- Expand trigger -->
            <button
              type="button"
              class="flex items-center gap-4 w-full sm:flex-1 min-w-0 text-left"
              onclick={() => toggleExpand(category.id)}
            >
              <div class="w-10 h-10 shrink-0 rounded-xl bg-base-200 flex items-center justify-center text-base-content transition-all duration-300 {expandedCategories.includes(category.id) ? 'rotate-90 text-primary bg-primary/10' : ''}">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div class="min-w-0">
                <h3 class="text-sm font-bold text-base-content uppercase tracking-tight truncate">{category.name}</h3>
                <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span class="text-xs font-mono text-base-content/50">/{category.slug}</span>
                  <span class="badge text-xs text-primary font-bold bg-primary/10 border-0 uppercase">
                    {category._count?.products || 0} Products
                  </span>
                </div>
              </div>
            </button>
            <!-- Action buttons -->
            <div class="flex items-center gap-2 w-full sm:w-auto justify-end sm:justify-start">
              <button type="button" onclick={() => openModal('subcategory', 'create', null, category.id)} class="btn btn-xs btn-ghost text-base-content/60 hover:text-primary">+ Add Sub</button>
              <button type="button" onclick={() => openModal('category', 'edit', category)} class="btn btn-xs btn-ghost text-base-content/60 hover:text-primary">Rename</button>
              <button type="button" onclick={() => itemToDelete = { mode: 'category', id: category.id, name: category.name }} class="btn btn-xs btn-ghost text-error hover:bg-error/10">Delete</button>
            </div>
          </div>

          <!-- Subcategories -->
          {#if expandedCategories.includes(category.id)}
            <div class="p-6 pl-6 sm:pl-16 space-y-3 bg-base-200/20 border-t border-base-300">
              {#if !category.subcategories || category.subcategories.length === 0}
                <div class="flex items-center gap-3 py-6 border border-dashed border-base-300 rounded-2xl justify-center bg-base-100">
                  <p class="text-xs font-bold text-base-content/40 uppercase tracking-widest">No subcategories</p>
                  <button onclick={() => openModal('subcategory', 'create', null, category.id)} class="text-xs font-bold text-primary uppercase underline bg-transparent border-0 cursor-pointer">Add</button>
                </div>
              {:else}
                {#each category.subcategories as sub}
                  <div class="group flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-base-100 border border-base-300 rounded-2xl hover:border-primary/40 hover:shadow-md transition-all gap-3">
                    <div class="flex items-center gap-3">
                      <div class="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_var(--color-primary)]"></div>
                      <div>
                        <p class="text-xs font-bold text-base-content uppercase tracking-wide">{sub.name}</p>
                        <p class="text-[10px] text-base-content/50 font-mono">/{sub.slug}</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 justify-end sm:justify-start">
                      <button onclick={() => openModal('subcategory', 'edit', sub, category.id)} class="btn btn-xs btn-ghost text-base-content/60 hover:text-primary">Rename</button>
                      <button onclick={() => itemToDelete = { mode: 'subcategory', id: sub.id, name: sub.name }} class="btn btn-xs btn-ghost text-error hover:bg-error/10">Delete</button>
                    </div>
                  </div>
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </div>
</div>

<!-- Create / Edit Modal -->
{#if isModalOpen}
  <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div class="card bg-base-100 border border-base-300 p-6 space-y-6 shadow-2xl w-full max-w-md">
      <div class="flex items-center justify-between">
        <h3 class="card-title text-lg font-bold tracking-tight text-base-content">
          {config.action === 'create' ? 'Create' : 'Edit'} {config.mode === 'category' ? 'Category' : 'Subcategory'}
        </h3>
        <button onclick={() => isModalOpen = false} class="btn btn-sm btn-circle btn-ghost text-base-content/75">✕</button>
      </div>
      <form onsubmit={handleSubmit} class="space-y-4">
        <div class="form-control w-full">
          <label for="taxName" class="label">
            <span class="label-text text-xs font-semibold text-base-content/60 uppercase tracking-wider">Name</span>
          </label>
          <Input type="text" id="taxName" required bind:value={taxName} placeholder="e.g. Incense & Herbs" />
        </div>
        <div class="form-control w-full">
          <label for="taxSlug" class="label">
            <span class="label-text text-xs font-semibold text-base-content/60 uppercase tracking-wider">Slug</span>
          </label>
          <Input type="text" id="taxSlug" required bind:value={taxSlug} placeholder="e.g. incense-herbs" />
        </div>
        <div class="flex items-center justify-end gap-3 pt-4 border-t border-base-300">
          <Button type="button" onclick={() => isModalOpen = false} variant="outline" size="sm">Cancel</Button>
          <Button type="submit" disabled={isSaving} variant="primary" size="sm">
            {isSaving ? 'Saving...' : config.action === 'create' ? 'Create' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Delete Confirmation Dialog -->
{#if itemToDelete}
  <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div class="card bg-base-100 border border-base-300 p-6 space-y-6 shadow-2xl w-full max-w-md">
      <div class="space-y-2">
        <h3 class="text-lg font-bold text-error">Delete {itemToDelete.mode === 'category' ? 'Category' : 'Subcategory'}</h3>
        <p class="text-xs text-base-content/60">
          Are you sure you want to permanently delete "{itemToDelete.name}"? This will disrupt all product associations and cannot be reversed.
        </p>
      </div>
      <div class="flex items-center justify-end gap-3 pt-4 border-t border-base-300">
        <Button type="button" onclick={() => itemToDelete = null} variant="outline" size="sm">Cancel</Button>
        <Button type="button" onclick={handleConfirmDelete} disabled={isDeleting} variant="error" size="sm">
          {isDeleting ? 'Deleting...' : 'Confirm Delete'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn { animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
</style>
